import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Button } from '~/client/components/ui/button'
import { Input } from '~/client/components/ui/input'
import { Label } from '~/client/components/ui/label'
import { toast } from 'sonner'
import { useForm } from '@tanstack/react-form'
import { resetPassword } from '~/server/common/supabase/auth'

export const Route = createFileRoute('/_app/reset-password')({
  component: RouteComponent,
})

type ResetPasswordValues = {
  newPassword: string
  confirmPassword: string
}

function RouteComponent() {
  const navigate = useNavigate()

  const defaultValues: ResetPasswordValues = {
    newPassword: '',
    confirmPassword: '',
  }

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      if (value.newPassword !== value.confirmPassword) {
        toast.error('New passwords do not match', {
          position: 'bottom-right',
          richColors: true,
        })
        return
      }

      try {
        await resetPassword(value.newPassword)

        toast.success('Password reset successfully', {
          position: 'bottom-right',
          richColors: true,
        })

        // Navigate to login or home after successful reset
        navigate({ to: '/' })
      } catch (err: any) {
        const message = err?.message || 'Failed to reset password'
        toast.error(message, {
          position: 'bottom-right',
          richColors: true,
        })
      }
    },
  })

  return (
    <div className="p-6 md:p-10 flex flex-col items-center justify-center">
      <div className="w-full max-w-md">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="flex flex-col gap-6"
        >
          <h1 className="text-2xl font-bold text-[#18003D]">Reset Password</h1>
          <p className="text-sm text-gray-600">Enter your new password below.</p>

          <div className="grid gap-3">
            <Label htmlFor="newPassword">New Password</Label>
            <form.Field
              name="newPassword"
              validators={{
                onChange: ({ value }) => (!value ? 'New password is required' : value.length < 8 ? 'Password must be at least 8 characters' : undefined),
              }}
              children={(field) => (
                <>
                  <Input
                    id={field.name}
                    type="password"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={field.state.meta.errors.length ? 'true' : 'false'}
                  />
                  {field.state.meta.errors[0] ? <span className="text-sm text-destructive">{field.state.meta.errors[0]}</span> : null}
                </>
              )}
            />
          </div>

          <div className="grid gap-3">
            <Label htmlFor="confirmPassword">Confirm New Password</Label>
            <form.Field
              name="confirmPassword"
              validators={{
                onChange: ({ value }) => (!value ? 'Please confirm your new password' : undefined),
              }}
              children={(field) => (
                <>
                  <Input
                    id={field.name}
                    type="password"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={field.state.meta.errors.length ? 'true' : 'false'}
                  />
                  {field.state.meta.errors[0] ? <span className="text-sm text-destructive">{field.state.meta.errors[0]}</span> : null}
                </>
              )}
            />
          </div>

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting] as const}
            children={([canSubmit, isSubmitting]) => (
              <div className="flex gap-3">
                <Button type="submit" className="bg-[#8A3DFF] hover:bg-[#7B2FE6]" disabled={!canSubmit || isSubmitting}>
                  {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
                </Button>
              </div>
            )}
          />
        </form>
      </div>
    </div>
  )
}
